import { filterDepartmentsForUi, resolveDepartmentDisplayName } from './departmentUi';
import { departmentIdScopeKey } from '../utils/normalizeIds';
import { isAllowedSearchHref, type SiteSearchHit } from './searchText';

export type IntraSearchPageInput = {
  id: string;
  label: string;
  href: string;
  hint?: string;
};

export type IntraSearchDepartmentInput = {
  id: string;
  name?: string | null;
};

export type IntraSearchFormInput = {
  id: string;
  label: string;
  href: string;
};

export type IntraMenuLink = {
  key: string;
  label: string;
  to?: string;
  href?: string;
  subItems?: ReadonlyArray<{ key: string; label: string; to: string }>;
};

const DROPPED_DEPARTMENT_SCOPE_KEYS = new Set(['OPD_PRIMARY', 'OPD_PC']);

export function collectIntraPageLinks(entries: readonly IntraMenuLink[]): IntraSearchPageInput[] {
  const pages: IntraSearchPageInput[] = [];
  const seen = new Set<string>();
  const push = (id: string, label: string, href: string | undefined): void => {
    const trimmed = href?.trim() ?? '';
    if (!trimmed || !label.trim() || !isAllowedSearchHref(trimmed) || seen.has(trimmed)) return;
    seen.add(trimmed);
    pages.push({ id, label, href: trimmed });
  };

  for (const entry of entries) {
    push(entry.key, entry.label, entry.to ?? entry.href);
    for (const sub of entry.subItems ?? []) {
      push(sub.key, sub.label, sub.to);
    }
  }
  return pages;
}

export function prepareIntraSearchDepartments(
  hospitalDepartments: readonly IntraSearchDepartmentInput[],
  scopeDepartmentIds: readonly string[],
): Array<{ id: string; label: string }> {
  if (!scopeDepartmentIds.length) return [];
  const wildcard = scopeDepartmentIds.some((id) => departmentIdScopeKey(id) === '*');
  const scopeKeys = new Set(scopeDepartmentIds.map((id) => departmentIdScopeKey(id)));
  const inScope = wildcard
    ? [...hospitalDepartments]
    : hospitalDepartments.filter((dept) => scopeKeys.has(departmentIdScopeKey(dept.id)));
  return filterDepartmentsForUi(inScope)
    .filter((dept) => !DROPPED_DEPARTMENT_SCOPE_KEYS.has(departmentIdScopeKey(dept.id)))
    .map((dept) => ({
      id: dept.id,
      label: resolveDepartmentDisplayName(dept.id, dept.name ?? '', null),
    }));
}

export function resolveDepartmentSearchAction(
  pathname: string,
  search: string,
): { href: string; kind: 'team' | 'ethic' | 'nurseNotes'; waitForCategoryId: string | null } {
  if (pathname === '/team' || pathname === '/team/ethic') {
    return { href: pathname, kind: 'team', waitForCategoryId: null };
  }
  const category = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).get('category');
  if (pathname === '/report' && (category === 'ethicSurveys' || category === 'orgClimateSurveys')) {
    return {
      href: `/report?category=${category}`,
      kind: 'ethic',
      waitForCategoryId: category,
    };
  }
  return {
    href: '/report?category=nurse_notes',
    kind: 'nurseNotes',
    waitForCategoryId: 'nurse_notes',
  };
}

export function buildIntraSearchIndex(input: {
  pages: readonly IntraSearchPageInput[];
  departments: readonly { id: string; label: string }[];
  departmentHref: string;
  forms: readonly IntraSearchFormInput[];
}): SiteSearchHit[] {
  const hits: SiteSearchHit[] = [];
  const pageHrefs = new Set<string>();

  for (const page of input.pages) {
    const href = page.href.trim();
    if (!isAllowedSearchHref(href) || pageHrefs.has(href)) continue;
    pageHrefs.add(href);
    hits.push({
      id: `page-${page.id}`,
      label: page.label,
      hint: page.hint ?? '',
      href,
      group: 'หน้า',
    });
  }

  const departmentHref = input.departmentHref.trim();
  if (isAllowedSearchHref(departmentHref)) {
    for (const dept of input.departments) {
      hits.push({
        id: `dept-${dept.id}`,
        label: dept.label,
        hint: '',
        href: departmentHref,
        group: 'หน่วยงาน',
        departmentId: dept.id,
      });
    }
  }

  for (const form of input.forms) {
    const href = form.href.trim();
    if (!isAllowedSearchHref(href) || pageHrefs.has(href)) continue;
    hits.push({
      id: `form-${form.id}`,
      label: form.label,
      hint: '',
      href,
      group: 'แบบฟอร์ม',
    });
  }

  return hits;
}
